import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Cpu, GitPullRequest, Check, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { SEVERITY, STATUS } from "@/lib/security";
import api from "@/lib/api";

const SEV_FILTERS = ["all", "critical", "high", "medium", "low"];

export default function Findings() {
  const [findings, setFindings] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [analysis, setAnalysis] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/findings");
    setFindings(data);
  };
  useEffect(() => { load(); }, []);

  const openFinding = async (f) => {
    setSelected(f);
    setAnalysis(f.ai_analysis || "");
  };

  const runAnalysis = async () => {
    if (!selected) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/findings/${selected.finding_id}/analyze`);
      setAnalysis(data.analysis);
      setFindings((prev) => prev.map((x) => x.finding_id === selected.finding_id ? { ...x, ai_analysis: data.analysis } : x));
    } catch {
      toast.error("Analysis failed. Try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const setStatus = async (status) => {
    const { data } = await api.patch(`/findings/${selected.finding_id}`, { status });
    setSelected(data);
    setFindings((prev) => prev.map((x) => x.finding_id === data.finding_id ? data : x));
    toast.success(`Marked as ${STATUS[status].label}`);
  };

  if (!findings) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-cyber" size={30} /></div>;
  }

  const visible = filter === "all" ? findings : findings.filter((f) => f.severity === filter);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">Findings</h1>
        <p className="text-sm text-muted mt-1">Prioritized security findings across code, cloud and dependencies.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {SEV_FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-colors capitalize ${
              filter === f ? "border-cyber text-cyber bg-cyber/10" : "border-line text-muted hover:text-ink"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="card-surface rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-line text-xs font-mono uppercase tracking-wider text-muted">
          <div className="col-span-5">Finding</div>
          <div className="col-span-2">Severity</div>
          <div className="col-span-2">CVSS</div>
          <div className="col-span-3">Status</div>
        </div>
        {visible.map((f, i) => {
          const meta = SEVERITY[f.severity];
          const Icon = meta.icon;
          const st = STATUS[f.status] || STATUS.open;
          return (
            <motion.button
              key={f.finding_id}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
              onClick={() => openFinding(f)} data-testid={`finding-row-${i}`}
              className="w-full grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-b border-line text-left hover:bg-[var(--surface-2)] transition-colors items-center">
              <div className="md:col-span-5 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg }}>
                  <Icon size={16} style={{ color: meta.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{f.title}</p>
                  <p className="text-xs font-mono text-muted truncate">{f.location}</p>
                </div>
              </div>
              <div className="md:col-span-2">
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
              </div>
              <div className="md:col-span-2 font-mono text-sm text-ink">{f.cvss}</div>
              <div className="md:col-span-3">
                <Badge variant="outline" className="border-line" style={{ color: st.color }}>{st.label}</Badge>
              </div>
            </motion.button>
          );
        })}
        {visible.length === 0 && <p className="text-center text-sm text-muted py-12">No findings for this filter.</p>}
      </div>

      {/* Detail drawer */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-[var(--surface)] border-line w-full sm:max-w-xl overflow-y-auto" data-testid="finding-drawer">
          {selected && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                    style={{ color: SEVERITY[selected.severity].color, background: SEVERITY[selected.severity].bg }}>
                    {SEVERITY[selected.severity].label} · CVSS {selected.cvss}
                  </span>
                  <span className="text-xs font-mono text-muted">{selected.cwe}</span>
                </div>
                <SheetTitle className="font-display text-xl text-ink text-left">{selected.title}</SheetTitle>
              </SheetHeader>

              <div className="mt-5 space-y-5">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Info label="Category" value={selected.category} />
                  <Info label="Scanner" value={selected.scanner?.toUpperCase()} />
                  <Info label="Location" value={selected.location} mono />
                  <Info label="Exploitability" value={selected.exploitability} />
                </div>

                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted mb-1.5">Description</p>
                  <p className="text-sm text-ink leading-relaxed">{selected.description}</p>
                </div>

                {selected.code_snippet && (
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted mb-1.5">Proof of concept</p>
                    <pre className="text-xs bg-[#0B0E14] border border-line rounded-lg p-3 overflow-x-auto text-crit">{selected.code_snippet}</pre>
                  </div>
                )}

                {/* AI analysis */}
                <div className="rounded-xl border border-cyber/30 bg-cyber/5 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-cyber"><Cpu size={15} /> AI Security Analysis</span>
                    {!analysis && (
                      <Button size="sm" onClick={runAnalysis} disabled={analyzing} data-testid="analyze-btn"
                        className="bg-cyber text-[#0B0E14] hover:bg-cyber-dim h-8 font-semibold">
                        {analyzing ? <Loader2 size={14} className="animate-spin" /> : <><Sparkles size={14} className="mr-1.5" /> Analyze</>}
                      </Button>
                    )}
                  </div>
                  {analyzing && !analysis && <p className="text-sm text-muted flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Engineer analyzing finding…</p>}
                  {analysis ? (
                    <div className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{analysis}</div>
                  ) : !analyzing && (
                    <p className="text-sm text-muted">{selected.recommendation}</p>
                  )}
                </div>

                {selected.fix_snippet && (
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted mb-1.5 flex items-center gap-1.5">
                      <GitPullRequest size={13} className="text-secure" /> Suggested fix
                    </p>
                    <pre className="text-xs bg-[#0B0E14] border border-secure/30 rounded-lg p-3 overflow-x-auto text-secure">{selected.fix_snippet}</pre>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2 border-t border-line">
                  <Button onClick={() => setStatus("in_progress")} variant="outline" data-testid="status-inprogress"
                    className="border-high/40 text-high hover:bg-high/10">In Progress</Button>
                  <Button onClick={() => setStatus("remediated")} data-testid="status-remediated"
                    className="bg-secure text-white hover:bg-secure/90"><Check size={15} className="mr-1.5" /> Remediated</Button>
                  <Button onClick={() => setStatus("false_positive")} variant="ghost" data-testid="status-falsepositive"
                    className="text-muted"><X size={15} className="mr-1.5" /> False positive</Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <p className="text-xs font-mono uppercase tracking-wider text-muted mb-0.5">{label}</p>
      <p className={`text-sm text-ink ${mono ? "font-mono break-all" : ""}`}>{value || "—"}</p>
    </div>
  );
}
